/**
 * EcheancesRappelsPage — Administration → Échéances & rappels (socle Lot B, mig 211).
 *
 * Liste TOUTES les échéances déclarées par les briques (véhicules, concessions,
 * et demain RH/urbanisme), groupées par domaine. Pour chacune, la commune règle
 * 1 à 2 rappels, en jours / mois / années. Toujours au moins un rappel (une
 * échéance rappelle toujours). La liste n'est jamais créée à la main.
 *
 * Réservé admin (permission admin.notifications.manage).
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Badge, SectionEyebrow } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { EcheanceReglageEntree, RappelDelai, UniteRappel } from '@/lib/api';
import { CalendarClock, AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';
import { EcheancesPersoCard } from '@/components/echeances/EcheancesPersoCard';

const UNITES: { valeur: UniteRappel; label: string }[] = [
  { valeur: 'jours', label: 'jours' },
  { valeur: 'mois', label: 'mois' },
  { valeur: 'annees', label: 'ans' },
];

function libelleRappel(r: RappelDelai): string {
  const u = r.unite === 'annees' ? (r.quantite > 1 ? 'ans' : 'an') : r.unite;
  return `${r.quantite} ${u}`;
}

export function EcheancesRappelsPage() {
  const [entrees, setEntrees] = useState<EcheanceReglageEntree[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.admin.echeances();
      setEntrees(r.entrees);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function flash(msg: string) { setSuccess(msg); setTimeout(() => setSuccess(null), 2500); }

  async function enregistrer(code: string, rappels: RappelDelai[]) {
    setError(null);
    try {
      const maj = await api.admin.setReglageEcheance(code, rappels);
      setEntrees((prev) => prev.map((e) => (e.code === code ? maj : e)));
      flash('Délais enregistrés.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Enregistrement impossible'); }
  }

  // Regroupement par domaine (l'ordre est déjà géré côté serveur).
  const groupes = entrees.reduce<Record<string, EcheanceReglageEntree[]>>((acc, e) => {
    (acc[e.groupe] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <SectionEyebrow>Administration</SectionEyebrow>
      <div className="mb-1 flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-tech-fg" />
        <h1 className="font-display text-xl font-semibold text-ink">Échéances &amp; rappels</h1>
      </div>
      <p className="mb-5 max-w-2xl text-sm text-ink-muted">
        Pour chaque échéance suivie par le logiciel (contrôle technique, assurance,
        concession…), choisissez quand être prévenu. Vous pouvez régler jusqu'à deux
        rappels. Une échéance déclenche toujours au moins un rappel.
      </p>

      {/* M6 (retour Manon) — échéances créées à la main, en plus de celles des modules. */}
      <div className="mb-6"><EcheancesPersoCard titre="Échéances personnalisées" /></div>

      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" icon={CheckCircle2} className="mb-3">{success}</Alert>}

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : entrees.length === 0 ? (
        <Card padded><p className="text-sm text-ink-muted">Aucune échéance déclarée pour le moment. Les modules qui suivent des échéances (parc véhicules, cimetière…) les feront apparaître ici.</p></Card>
      ) : (
        Object.entries(groupes).map(([groupe, items]) => (
          <div key={groupe} className="mb-6">
            <h2 className="mb-2 font-display text-sm font-semibold uppercase tracking-wide text-ink-subtle">{groupe}</h2>
            <div className="space-y-2">
              {items.map((e) => (
                <EcheanceRow key={e.code} entree={e} onSave={(r) => enregistrer(e.code, r)} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ── Une ligne d'échéance réglable ───────────────────────────────────────────
function EcheanceRow({ entree, onSave }: {
  entree: EcheanceReglageEntree;
  onSave: (rappels: RappelDelai[]) => void;
}) {
  // État local des rappels (1 à 2). On part des délais effectifs.
  const [rappels, setRappels] = useState<RappelDelai[]>(entree.rappelsEffectifs.length ? entree.rappelsEffectifs : entree.rappelsDefaut);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRappels(entree.rappelsEffectifs.length ? entree.rappelsEffectifs : entree.rappelsDefaut);
    setDirty(false);
  }, [entree]);

  function maj(i: number, patch: Partial<RappelDelai>) {
    setRappels((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  }
  function ajouterSecond() {
    if (rappels.length >= 2) return;
    setRappels((prev) => [...prev, { quantite: 15, unite: 'jours' }]);
    setDirty(true);
  }
  function retirerSecond() {
    if (rappels.length <= 1) return; // jamais zéro rappel
    setRappels((prev) => prev.slice(0, 1));
    setDirty(true);
  }
  function reinitialiser() {
    // Envoi d'une liste vide = retour au défaut éditeur côté serveur.
    onSave([]);
  }

  const personnalise = entree.rappelsOverride != null;

  return (
    <Card padded>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">{entree.libelle}</span>
            {personnalise && <Badge variant="tech" size="sm">Personnalisé</Badge>}
          </div>
          <p className="text-2xs text-ink-subtle">{entree.declencheur}</p>
          <p className="mt-0.5 text-2xs text-ink-dim">Prévient : {entree.cibleLabel}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <span className="text-2xs font-medium text-ink-muted">Prévenir</span>
        {rappels.map((r, i) => (
          <div key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-2xs text-ink-dim">puis</span>}
            <input
              type="number" min={1} value={r.quantite}
              onChange={(ev) => maj(i, { quantite: Math.max(1, Number(ev.target.value) || 1) })}
              className="w-16 rounded-r-md border border-border bg-surface px-2 py-1 text-sm text-ink"
              aria-label={`Quantité du rappel ${i + 1}`}
            />
            <select
              value={r.unite}
              onChange={(ev) => maj(i, { unite: ev.target.value as UniteRappel })}
              className="rounded-r-md border border-border bg-surface px-2 py-1 text-sm text-ink"
              aria-label={`Unité du rappel ${i + 1}`}
            >
              {UNITES.map((u) => <option key={u.valeur} value={u.valeur}>{u.label}</option>)}
            </select>
            <span className="text-2xs text-ink-dim">avant</span>
          </div>
        ))}
        {rappels.length < 2
          ? <Button variant="ghost" size="sm" onClick={ajouterSecond}>+ 2ᵉ rappel</Button>
          : <Button variant="ghost" size="sm" onClick={retirerSecond} className="text-ink-muted">– retirer</Button>}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-2xs text-ink-dim">
          Par défaut : {entree.rappelsDefaut.map(libelleRappel).join(', puis ')} avant.
        </span>
        <div className="flex gap-1.5">
          {personnalise && (
            <Button variant="ghost" size="sm" onClick={reinitialiser} className="text-ink-muted">
              <RotateCcw className="h-3.5 w-3.5" /> Défaut
            </Button>
          )}
          <Button variant="primary" size="sm" disabled={!dirty} onClick={() => onSave(rappels)}>
            Enregistrer
          </Button>
        </div>
      </div>
    </Card>
  );
}
